import React from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, Switch, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { removeCondition, setConditionActive, useAlerts, useConditions } from '../../src/data';
import { summarize } from '../../src/format';
import { Action, Chip, Empty, Head, Label, Panel, Tag } from '../../src/ui';
import { D } from '../../src/theme';
import type { Condition } from '../../src/types';

/**
 * 내 조건 — 홈과 같은 문법으로.
 *
 * 조건 하나당 판 하나. 무엇을 감시 중인지가 칩으로 펼쳐지고, 상태는 색이 아니라
 * 말과 반전으로 읽힌다. 답을 기다리는 조건만 형광으로 띄워 할 일이 보이게 한다.
 */
const CHIP_LIMIT = 6;

export default function Conditions() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const conds = useConditions();
  const alerts = useAlerts();
  const pending = conds.filter((c) => c.active && c.needs_confirm).length;

  function confirmRemove(c: Condition) {
    Alert.alert('조건 삭제', `"${c.text}" 를 지울까요?`, [
      { text: '취소', style: 'cancel' },
      { text: '삭제', style: 'destructive', onPress: () => removeCondition(c.id).catch(() => {}) },
    ]);
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: D.ground }}
      contentContainerStyle={{ paddingTop: insets.top + 14, paddingBottom: 28, gap: 10 }}
    >
      <Head
        kicker={pending ? `${pending}개는 답을 기다려요` : '조건에 맞는 딜이 뜨면 알려드려요'}
        title={`내 조건${conds.length ? ` ${conds.length}` : ''}`}
        right={<Chip label="+ 새 조건" on onPress={() => router.push('/(tabs)/search')} />}
      />

      <View style={{ paddingHorizontal: 16, gap: 10 }}>
        {conds.length === 0 ? (
          <Panel style={{ gap: 12 }}>
            <Empty
              title="아직 감시 중인 게 없어요"
              body={'상품 이름을 몰라도 됩니다. "알약이랑 물약 같이 먹는 영양제" 처럼\n설명만 적으면 AI 가 되물어서 같이 좁혀 줘요.'}
            />
            <Action label="첫 조건 만들기" onPress={() => router.push('/(tabs)/search')} />
          </Panel>
        ) : (
          conds.map((c) => (
            <Card
              key={c.id}
              cond={c}
              count={alerts.filter((a) => a.condition_id === c.id).length}
              onOpen={() => router.push(`/condition/${c.id}`)}
              onRemove={() => confirmRemove(c)}
            />
          ))
        )}
        {conds.length > 0 ? <Text style={s.hint}>카드를 길게 누르면 삭제할 수 있어요</Text> : null}
      </View>
    </ScrollView>
  );
}

function statusOf(c: Condition): { text: string; tone: 'ink' | 'volt' | 'quiet' } {
  if (!c.active) return { text: '꺼둠', tone: 'quiet' };
  if (!c.parsed) return { text: 'AI 가 읽는 중', tone: 'quiet' };
  if (c.needs_confirm) return { text: '확인 필요 · 알림 보류', tone: 'volt' };
  return { text: '감시 중', tone: 'ink' };
}

function Card({
  cond,
  count,
  onOpen,
  onRemove,
}: {
  cond: Condition;
  count: number;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const st = statusOf(cond);
  const { products, rules } = summarize(cond.spec as Record<string, unknown> | undefined);
  const shown = products.slice(0, CHIP_LIMIT);
  const more = products.length - shown.length;
  const off = !cond.active;

  return (
    <Pressable onPress={onOpen} onLongPress={onRemove} style={({ pressed }) => [s.card, cond.needs_confirm && cond.active && s.cardWarn, pressed && { opacity: 0.9 }]}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
        <View style={{ flex: 1, minWidth: 0, gap: 7 }}>
          <Text style={[s.title, off && { color: D.ink3 }]} numberOfLines={2}>
            {cond.text}
          </Text>
          <View style={{ flexDirection: 'row' }}>
            <Tag label={st.text} tone={st.tone} />
          </View>
        </View>
        <Switch
          value={cond.active}
          onValueChange={(v) => setConditionActive(cond.id, v).catch(() => {})}
          trackColor={{ true: D.ink, false: D.line }}
          thumbColor={cond.active ? D.volt : '#FFFFFF'}
        />
      </View>

      {shown.length ? (
        <View style={{ gap: 6 }}>
          <Label>찾는 제품</Label>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
            {shown.map((k) => (
              <View key={k} style={[s.pill, off && { borderColor: D.line }]}>
                <Text style={[s.pillText, off && { color: D.ink3 }]} numberOfLines={1}>
                  {k}
                </Text>
              </View>
            ))}
            {more > 0 ? <Text style={s.more}>+{more}개</Text> : null}
          </View>
        </View>
      ) : null}

      {rules.length ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
          {rules.map((r) => (
            <View key={r} style={s.rule}>
              <Text style={s.ruleText}>{r}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {cond.needs_confirm && cond.active ? (
        <Text style={s.act}>답하고 감시 시작하기 →</Text>
      ) : count > 0 ? (
        <Text style={s.count}>
          지금까지 알림 <Text style={{ color: D.ink, fontWeight: '900' }}>{count}건</Text>
        </Text>
      ) : null}
    </Pressable>
  );
}

const s = StyleSheet.create({
  card: { backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, padding: 14, gap: 11 },
  cardWarn: { backgroundColor: '#FBFFCE' },
  title: { fontSize: 15.5, fontWeight: '800', lineHeight: 21, color: D.ink },

  pill: { borderWidth: 1, borderColor: D.ink, paddingVertical: 4, paddingHorizontal: 8, maxWidth: '100%' },
  pillText: { fontSize: 12, fontWeight: '800', color: D.ink },
  more: { fontSize: 12, fontWeight: '700', color: D.ink3, alignSelf: 'center' },
  rule: { backgroundColor: D.line, paddingVertical: 4, paddingHorizontal: 8 },
  ruleText: { fontSize: 12, fontWeight: '700', color: D.ink2, fontVariant: ['tabular-nums'] },

  act: { fontSize: 12.5, fontWeight: '900', color: D.ink, borderTopWidth: 1, borderTopColor: D.line, paddingTop: 10 },
  count: { fontSize: 12, color: D.ink2, borderTopWidth: 1, borderTopColor: D.line, paddingTop: 10, fontVariant: ['tabular-nums'] },
  hint: { fontSize: 11, color: D.ink3, textAlign: 'center', marginTop: 2 },
});
